import React, { useState, useEffect } from 'react';
import { useJournalStore } from '../../store/journalStore';
import { useAuth } from '../auth/AuthProvider';
import LoadingSpinner from '../LoadingSpinner';
import ErrorDisplay from '../common/ErrorDisplay';
import { Plus, ArrowLeft, Feather } from 'lucide-react';
import { JournalEntry, JournalTemplateType } from '../../types';
import { JOURNAL_TEMPLATES } from './JournalTemplates';
import TemplateSelector from './TemplateSelector';
import DynamicForm from './DynamicForm';
import JournalBook from './JournalBook';

export default function JournalPage() {
  const { user } = useAuth();
  const { entries, isLoading, error: storeError, actions } = useJournalStore();
  const [selectedTemplate, setSelectedTemplate] = useState<JournalTemplateType>('brain-dump');
  const [showTemplateSelector, setShowTemplateSelector] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    if (user) {
      actions.fetchEntries();
    }
  }, [user, actions]);

  const handleSubmit = async (data: Record<string, any>) => {
    try {
      if (selectedEntry) {
        await actions.updateEntry(selectedEntry.id, data);
      } else {
        await actions.addEntry(data);
      }
      setSelectedEntry(null);
      setShowTemplateSelector(true);
      setViewMode(false);
    } catch (err) {
      console.error('Error saving entry:', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await actions.deleteEntry(id);
      setSelectedEntry(null);
      setShowTemplateSelector(true);
      setViewMode(false);
    } catch (err) {
      console.error('Error deleting entry:', err);
    }
  };

  const handleEdit = (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setSelectedTemplate(entry.template_type || 'brain-dump');
    setShowTemplateSelector(false);
    setViewMode(false);
  };

  // Find the selected template object
  const currentTemplate = JOURNAL_TEMPLATES.find(t => t.id === selectedTemplate) || {
    id: 'quick',
    title: 'Quick Entry',
    description: 'Write freely without prompts',
    icon: <Feather className="h-5 w-5" />,
    sections: [] // Empty sections for free-form writing
  };

  if (isLoading) return <LoadingSpinner />;

  if (storeError) {
    return (
      <ErrorDisplay
        error={{
          code: 'journal_error',
          message: storeError,
          details: 'Please try again or contact support if the problem persists.'
        }}
        onRetry={actions.fetchEntries}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-8">
      {/* Writing Interface */}
      <div className="bg-white rounded-xl shadow-sm border border-mint-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-midnight-800 mb-2">
                {selectedEntry ? 'Edit Entry' : 'Journal Entry'}
              </h1>
              <p className="text-midnight-600">
                {showTemplateSelector ? 'Choose a template to start your entry' : 'Express what\'s on your mind'}
              </p>
            </div>
            {!showTemplateSelector && (
              <button
                onClick={() => {
                  setSelectedEntry(null);
                  setSelectedTemplate('brain-dump');
                  setShowTemplateSelector(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 text-midnight-600 hover:text-midnight-800 transition-colors"
              >
                <Plus className="h-5 w-5" />
                <span>New Entry</span>
              </button>
            )}
          </div>

          {showTemplateSelector ? (
            <TemplateSelector
              selectedTemplate={selectedTemplate}
              onSelect={(template) => {
                setSelectedTemplate(template);
                setShowTemplateSelector(false);
              }}
            />
          ) : (
            <DynamicForm
              template={currentTemplate}
              onSubmit={handleSubmit}
              initialData={selectedEntry}
              onBack={() => {
                setShowTemplateSelector(true);
                setSelectedEntry(null);
              }}
              isViewMode={false}
            />
          )}
        </div>
      </div>

      {/* Journal Book */}
      <div>
        <h2 className="text-2xl font-bold text-midnight-800 mb-6">Your Journal</h2>
        <JournalBook
          entries={entries}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
}